// Settings → Toolkit rules (rbac-restore R3 3.1): connector-wide allow/deny rows over
// gateway/api/policy.py. Every route is `manage_policy`; the section is absent without it.
//
//   GET    /workspaces/{id}/connector-policies            → {policies}
//   PUT    /workspaces/{id}/connector-policies            ← {connector_name, effect}
//   DELETE /workspaces/{id}/connector-policies/{policy_id}
//
// The PUT is an upsert keyed on connector_name (connector-wide; the route rejects a
// tool_slug), so "change effect" is the same PUT with the other effect.
//
// Allowlist mode (rbac-restore R3 3.1, deferred until GET /workspaces/{id} returned the
// current `allowlist_mode`; built in simple-mode S4):
//
//   PATCH /workspaces/{id}/allowlist-mode  ← {allowlist_mode: bool}   manage_policy +
//                                            entitlement `policy.allowlist_mode`
//
// The switch shows the stored state, needs an explicit confirm (turning it on blocks every
// toolkit without an allow rule), then fires exactly one PATCH. When the plan lacks
// `policy.allowlist_mode` (or the plan is still loading / unreadable: fail closed) the
// switch is disabled with the server's remedy and the upsell link.
import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { errorMessage } from '../api/errors'
import { qk } from '../api/queries'
import type { ConnectorPoliciesResponse, ConnectorPolicy, Workspace } from '../api/types'
import { useSession } from '../context/SessionContext'
import { useCan } from '../rbac'
import { useFeature } from '../entitlements'
import { PlanNote } from '../entitlements/PlanNote'
import { ConfirmAction } from '../components/ConfirmAction'
import { Badge, QueryState } from '../components/ui'

type Effect = 'allow' | 'deny'

function useUpsert(workspaceId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { connector_name: string; effect: Effect }) =>
      apiFetch<ConnectorPolicy>(`/workspaces/${workspaceId}/connector-policies`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.connectorPolicies(workspaceId) }),
  })
}

function AddRuleForm({ workspaceId }: { workspaceId: string | null }) {
  const [name, setName] = useState('')
  const [effect, setEffect] = useState<Effect>('deny')
  const upsert = useUpsert(workspaceId)
  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    const connector_name = name.trim()
    if (!connector_name || upsert.isPending) return
    upsert.mutate({ connector_name, effect }, { onSuccess: () => setName('') })
  }
  return (
    <form onSubmit={onSubmit} aria-label="Add toolkit rule" className="field-row">
      <div className="field field--compact">
        <label htmlFor="policy-connector">Toolkit</label>
        <input
          id="policy-connector"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. slack"
          maxLength={128}
        />
      </div>
      <div className="field field--compact">
        <label htmlFor="policy-effect">Effect</label>
        <select id="policy-effect" value={effect} onChange={(e) => setEffect(e.target.value as Effect)}>
          <option value="deny">Deny</option>
          <option value="allow">Allow</option>
        </select>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={!name.trim() || upsert.isPending} aria-busy={upsert.isPending}>
          {upsert.isPending ? 'Saving…' : 'Add rule'}
        </button>
      </div>
      {upsert.error ? <p className="form-error" role="alert">{errorMessage(upsert.error)}</p> : null}
    </form>
  )
}

function RuleRow({ policy, workspaceId, onDelete }: { policy: ConnectorPolicy; workspaceId: string | null; onDelete: () => void }) {
  const upsert = useUpsert(workspaceId)
  const other: Effect = policy.effect === 'allow' ? 'deny' : 'allow'
  return (
    <tr>
      <td className="cell-strong">{policy.connector_name}</td>
      <td>
        <Badge tone={policy.effect === 'allow' ? 'success' : 'error'}>{policy.effect === 'allow' ? 'Allow' : 'Deny'}</Badge>
        {upsert.error ? <div className="form-error" role="alert">{errorMessage(upsert.error)}</div> : null}
      </td>
      <td className="cell-actions">
        <button
          type="button"
          className="btn btn--sm"
          disabled={upsert.isPending}
          aria-busy={upsert.isPending}
          onClick={() => upsert.mutate({ connector_name: policy.connector_name, effect: other })}
        >
          {other === 'allow' ? 'Change to allow' : 'Change to deny'}
        </button>
        <button
          type="button"
          className="btn btn--sm btn--ghost-danger"
          aria-label={`Delete rule for ${policy.connector_name}`}
          onClick={onDelete}
        >
          Delete
        </button>
      </td>
    </tr>
  )
}

function PolicyRules() {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const [deleting, setDeleting] = useState<ConnectorPolicy | null>(null)
  const q = useQuery({
    queryKey: qk.connectorPolicies(workspaceId),
    queryFn: () => apiFetch<ConnectorPoliciesResponse>(`/workspaces/${workspaceId}/connector-policies`),
    enabled: !!workspaceId,
  })
  const remove = useMutation({
    mutationFn: (p: ConnectorPolicy) =>
      apiFetch(`/workspaces/${workspaceId}/connector-policies/${p.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.connectorPolicies(workspaceId) })
      setDeleting(null)
    },
  })
  const policies = [...(q.data?.policies ?? [])].sort((a, b) => a.connector_name.localeCompare(b.connector_name))

  return (
    <QueryState isLoading={q.isLoading} error={q.error} what="toolkit rules" onRetry={() => q.refetch()}>
      {policies.length === 0 ? (
        <p className="cell-sub">No rules yet: every connected toolkit can run.</p>
      ) : (
        <div className="table-wrap">
          <table className="table table--dense" aria-label="Toolkit rules">
            <thead>
              <tr>
                <th>Toolkit</th>
                <th>Effect</th>
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => (
                <RuleRow key={p.id} policy={p} workspaceId={workspaceId} onDelete={() => { remove.reset(); setDeleting(p) }} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AddRuleForm workspaceId={workspaceId} />
      {deleting && (
        <ConfirmAction
          title="Delete toolkit rule"
          confirmLabel="Delete rule"
          pendingLabel="Deleting…"
          pending={remove.isPending}
          error={remove.error ? errorMessage(remove.error) : null}
          onConfirm={() => remove.mutate(deleting)}
          onClose={() => setDeleting(null)}
        >
          <p>
            Delete the <strong>{deleting.effect}</strong> rule for <strong>{deleting.connector_name}</strong>? The
            toolkit falls back to the workspace default.
          </p>
        </ConfirmAction>
      )}
    </QueryState>
  )
}

function AllowlistMode() {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const plan = useFeature('policy.allowlist_mode')
  const [confirming, setConfirming] = useState(false)
  const ws = useQuery({
    queryKey: qk.workspace(workspaceId),
    queryFn: () => apiFetch<Workspace>(`/workspaces/${workspaceId}`),
    enabled: !!workspaceId,
  })
  const on = ws.data?.allowlist_mode === true
  const toggle = useMutation({
    mutationFn: (next: boolean) =>
      apiFetch<{ workspace_id: string; allowlist_mode: boolean }>(`/workspaces/${workspaceId}/allowlist-mode`, {
        method: 'PATCH',
        body: JSON.stringify({ allowlist_mode: next }),
      }),
    onSuccess: (res, next) => {
      const value = typeof res?.allowlist_mode === 'boolean' ? res.allowlist_mode : next
      qc.setQueryData<Workspace>(qk.workspace(workspaceId), (old) => (old ? { ...old, allowlist_mode: value } : old))
      setConfirming(false)
    },
  })
  const known = typeof ws.data?.allowlist_mode === 'boolean'
  return (
    <div className="allowlist-mode">
      <QueryState isLoading={ws.isLoading} error={ws.error} what="the workspace’s allowlist mode" onRetry={() => ws.refetch()}>
        <label className="check">
          <input
            type="checkbox"
            role="switch"
            aria-checked={on}
            checked={on}
            disabled={!known || !plan.allowed || toggle.isPending}
            aria-describedby={!plan.allowed && !plan.loading ? 'allowlist-plan' : undefined}
            onChange={() => {
              toggle.reset()
              setConfirming(true)
            }}
          />{' '}
          Allowlist mode {known ? (on ? '(on)' : '(off)') : ''}
        </label>
        <p className="cell-sub">
          {on
            ? 'Only toolkits with an allow rule run. Everything else is blocked.'
            : 'Every toolkit runs unless a rule denies it.'}
        </p>
        <PlanNote gate={plan} id="allowlist-plan" />
      </QueryState>
      {confirming && (
        <ConfirmAction
          title={on ? 'Turn off allowlist mode' : 'Turn on allowlist mode'}
          confirmLabel={on ? 'Turn off' : 'Turn on'}
          pendingLabel="Saving…"
          pending={toggle.isPending}
          error={toggle.error ? errorMessage(toggle.error) : null}
          onConfirm={() => toggle.mutate(!on)}
          onClose={() => setConfirming(false)}
        >
          {on ? (
            <p>Every toolkit will run again unless a rule denies it.</p>
          ) : (
            <p>
              Only toolkits with an <strong>allow</strong> rule will run. Every other toolkit is blocked for every agent in
              this workspace until you add an allow rule for it.
            </p>
          )}
        </ConfirmAction>
      )}
    </div>
  )
}

/** Rendered inside Settings; absent unless the caller holds `manage_policy`. */
export function PolicySection() {
  const canManage = useCan('manage_policy')
  if (!canManage) return null
  return (
    <section className="settings-section" aria-labelledby="policy-h">
      <h2 id="policy-h">Toolkit rules</h2>
      <p>
        Allow or deny a whole toolkit for every agent in this workspace. A denied toolkit’s tools don’t run.
      </p>
      <AllowlistMode />
      <PolicyRules />
    </section>
  )
}
